import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, History, Info, MessageCircle, RotateCcw } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  descargarArchivoActividad,
  tieneArchivoAdjunto,
  type ArchivoActividadRef,
} from "@/features/programas/components/DescargaArchivoActividad";
import type { ChatBoxRef } from "@/features/evaluacion/components/ChatBox";
import { ChatBox } from "@/features/evaluacion/components/ChatBox";
import { useUpdatePreguntasUsadas } from "@/hooks/intentos/useIntentos";
import { useRetrosActividad } from "@/hooks/intentos/useRetrosActividad";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { getIntentosInfo } from "@/shared/utils/intentos";
import { IntentosBadge } from "@/shared/components/IntentosBadge";
import { cn } from "@/lib/utils";
import { DescargarRetroMenu } from "./feedback/DescargarRetroMenu";
import { ResizableSplit } from "./feedback/ResizableSplit";
import { RetroPanel } from "./feedback/RetroPanel";
import { CoachPanel, CoachRiel, PreguntasSugeridas } from "./feedback/CoachPanel";
import { IntentoSelector } from "./feedback/IntentoSelector";
import { accionPrincipal } from "./feedback/estilos";

const CLAVE_COACH_CONTRAIDO = "retroia.feedback.coachContraido";

function leerCoachContraido(): boolean {
  try {
    return localStorage.getItem(CLAVE_COACH_CONTRAIDO) === "1";
  } catch {
    return false;
  }
}

interface ActividadFeedbackViewProps {
  actividad: {
    id: number;
    nombre: string;
    limiteIntentos?: number;
    archivoWord?: ArchivoActividadRef | null;
  };
  cerebro: string;
  retroContent: string;
  sessionId: string | null;
  isStreaming: boolean;
  actividadAprendiz:
    | {
        id?: number;
        intentos?: number;
        intentosExtras?: number;
        actividadPrograma?: {
          programa?: { limiteIntentos?: number };
        };
        ultimoIntento?: {
          id?: number;
          preguntasSugeridas?: string[] | null;
        };
        historialChat?: { role: string; content: string }[] | null;
      }
    | null
    | undefined;
  preguntasUsadas: Set<string>;
  onPreguntaUsada: (updated: Set<string>) => void;
  onVolverAIntentar: () => void;
  onBack: () => void;
  isAnimating: boolean;
  retroRef: React.RefObject<HTMLDivElement | null>;
  chatBoxRef: React.RefObject<ChatBoxRef | null>;
}

type Pestana = "retro" | "coach";

export function ActividadFeedbackView({
  actividad,
  cerebro,
  retroContent,
  sessionId,
  isStreaming,
  actividadAprendiz,
  preguntasUsadas,
  onPreguntaUsada,
  onVolverAIntentar,
  onBack,
  isAnimating,
  retroRef,
  chatBoxRef,
}: ActividadFeedbackViewProps) {
  const esEscritorio = useMediaQuery("(min-width: 1024px)");
  const [pestana, setPestana] = useState<Pestana>("retro");
  const [coachContraido, setCoachContraido] = useState(leerCoachContraido);
  const [hayRespuestaNueva, setHayRespuestaNueva] = useState(false);
  const [composerEnfocado, setComposerEnfocado] = useState(false);
  const [intentoVisto, setIntentoVisto] = useState<number | null>(null);
  const { mutateAsync: updatePreguntasUsadas } = useUpdatePreguntasUsadas();
  const { data: retros = [], refetch: recargarRetros } = useRetrosActividad(
    actividadAprendiz?.id,
  );

  const coachVisible = esEscritorio ? !coachContraido : pestana === "coach";

  useEffect(() => {
    try {
      localStorage.setItem(CLAVE_COACH_CONTRAIDO, coachContraido ? "1" : "0");
    } catch {
      // Sin almacenamiento: el estado solo dura mientras la página esté abierta.
    }
  }, [coachContraido]);

  useEffect(() => {
    if (coachVisible) setHayRespuestaNueva(false);
  }, [coachVisible]);

  // Al terminar una evaluación nueva: recargar la lista y volver al intento más reciente.
  const estabaEvaluandoRef = useRef(isStreaming);
  useEffect(() => {
    if (estabaEvaluandoRef.current && !isStreaming) {
      setIntentoVisto(null);
      void recargarRetros();
    }
    if (isStreaming) setIntentoVisto(null);
    estabaEvaluandoRef.current = isStreaming;
  }, [isStreaming, recargarRetros]);

  const onRespuestaCompleta = useCallback(() => {
    if (!coachVisible) setHayRespuestaNueva(true);
  }, [coachVisible]);

  const handleSendPregunta = async (pregunta: string) => {
    if (!chatBoxRef.current || preguntasUsadas.has(pregunta) || !actividadAprendiz?.ultimoIntento?.id) return;
    const newSet = new Set(preguntasUsadas);
    newSet.add(pregunta);
    onPreguntaUsada(newSet);
    try {
      await updatePreguntasUsadas({
        idIntento: actividadAprendiz.ultimoIntento.id,
        preguntasUsadas: Array.from(newSet),
      });
    } catch (error) {
      console.error("Error al guardar preguntas usadas:", error);
      onPreguntaUsada(preguntasUsadas); // revert
    }
    await chatBoxRef.current.sendMessage(pregunta);
  };

  const preguntasSugeridas = actividadAprendiz?.ultimoIntento?.preguntasSugeridas;

  const intentosInfo = getIntentosInfo({
    intentos: actividadAprendiz?.intentos,
    intentosExtras: actividadAprendiz?.intentosExtras,
    limiteIntentos:
      actividadAprendiz?.actividadPrograma?.programa?.limiteIntentos ??
      actividad.limiteIntentos,
  });
  const canRetry = !isStreaming && !intentosInfo.agotados;

  const retroAnterior = retros.find((r) => r.id === intentoVisto);
  const ultimoNumero = retros[retros.length - 1]?.numero;
  const onDescargarReto = tieneArchivoAdjunto(actividad.archivoWord)
    ? () => void descargarArchivoActividad(actividad.archivoWord!)
    : undefined;

  const selector = (
    <IntentoSelector
      intentos={retros}
      seleccionado={intentoVisto}
      onSeleccionar={setIntentoVisto}
      disabled={isStreaming}
    />
  );

  const avisoIntentoAnterior = retroAnterior && (
    <div className="mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border border-[var(--primary)]/30 bg-[var(--primary)]/5 px-4 py-3 text-sm text-[var(--foreground)]">
      <History size={16} className="flex-shrink-0 text-[var(--primary)]" />
      <span className="flex-1">
        Estás viendo el intento {retroAnterior.numero}. Tu intento más reciente es el{" "}
        {ultimoNumero}.
      </span>
      <button
        type="button"
        onClick={() => setIntentoVisto(null)}
        className="font-semibold text-[var(--primary)] hover:underline"
      >
        Ver el más reciente
      </button>
    </div>
  );

  const chat = (
    <ChatBox
      ref={chatBoxRef}
      index={cerebro}
      idActividadAprendiz={actividadAprendiz?.id ?? 0}
      sessionId={sessionId}
      historialChat={
        (actividadAprendiz?.historialChat as {
          role: "user" | "assistant";
          content: string;
        }[]) ?? []
      }
      onRespuestaCompleta={onRespuestaCompleta}
      onFocoComposer={setComposerEnfocado}
      sugerencias={
        <PreguntasSugeridas
          preguntas={Array.isArray(preguntasSugeridas) ? preguntasSugeridas : []}
          usadas={preguntasUsadas}
          onEnviar={handleSendPregunta}
          disabled={isStreaming}
        />
      }
    />
  );

  const volverAIntentar = (className?: string) => (
    <Button
      size="sm"
      onClick={onVolverAIntentar}
      disabled={!canRetry}
      title={intentosInfo.agotados ? "Ya usaste todos tus intentos" : undefined}
      className={cn(accionPrincipal, className)}
    >
      <RotateCcw className="h-4 w-4" />
      Volver a intentar
    </Button>
  );

  const retro = (pie?: React.ReactNode, encabezado?: React.ReactNode) => (
    <RetroPanel
      contenido={retroAnterior ? retroAnterior.retro : retroContent}
      isStreaming={isStreaming && !retroAnterior}
      scrollRef={retroRef}
      aviso={
        <>
          {encabezado}
          {avisoIntentoAnterior}
        </>
      }
      pie={pie}
    />
  );

  return (
    <div
      className={`platform-theme-scope flex flex-1 min-h-0 flex-col ${
        isAnimating
          ? "transition-all duration-300 ease-in-out opacity-0 translate-y-4"
          : "opacity-100 translate-y-0"
      }`}
    >
      {/* Barra superior */}
      <div className="flex flex-shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--card-background)] px-4 py-2.5 lg:px-6">
        <button
          type="button"
          onClick={onBack}
          className="flex flex-shrink-0 items-center gap-0.5 text-sm font-medium text-[var(--primary)] hover:underline"
        >
          <ChevronLeft size={18} />
          Atrás
        </button>
        <h2 className="min-w-0 flex-1 truncate text-base font-bold text-[var(--foreground)] lg:text-lg">
          {actividad.nombre}
        </h2>
        <IntentosBadge
          info={intentosInfo}
          icon={<Info size={14} />}
          className="flex flex-shrink-0 items-center gap-1 rounded-full border border-[var(--border)] px-2.5 py-1 text-xs font-medium text-[var(--primary)] tabular-nums"
        />
        {esEscritorio && (
          <div className="flex flex-shrink-0 items-center gap-2">
            {selector}
            <DescargarRetroMenu
              idActividadAprendiz={actividadAprendiz?.id}
              actividadNombre={actividad.nombre}
              disabled={isStreaming}
              onDescargarReto={onDescargarReto}
            />
            {volverAIntentar()}
          </div>
        )}
      </div>

      {esEscritorio ? (
        <ResizableSplit
          etiquetaDivisor="Cambiar el tamaño del coach virtual"
          contraido={coachContraido}
          izquierda={retro()}
          derecha={
            <CoachPanel onContraer={() => setCoachContraido(true)}>{chat}</CoachPanel>
          }
          derechaContraida={
            <CoachRiel
              onExpandir={() => setCoachContraido(false)}
              hayRespuestaNueva={hayRespuestaNueva}
            />
          }
        />
      ) : (
        <>
          <div
            role="tablist"
            aria-label="Secciones de la actividad"
            className="flex flex-shrink-0 gap-1 border-b border-[var(--border)] bg-[var(--card-background)] p-2"
          >
            {(
              [
                ["retro", "Retroalimentación"],
                ["coach", "Coach virtual"],
              ] as const
            ).map(([id, etiqueta]) => (
              <button
                key={id}
                type="button"
                role="tab"
                id={`pestana-${id}`}
                aria-selected={pestana === id}
                aria-controls={`panel-${id}`}
                onClick={() => setPestana(id)}
                className={`relative flex-1 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  pestana === id
                    ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                    : "text-[var(--foreground)] opacity-60"
                }`}
              >
                {etiqueta}
                {id === "coach" && hayRespuestaNueva && (
                  <span
                    className="ml-1.5 inline-block h-2 w-2 rounded-full bg-[var(--primary)] align-middle"
                    aria-label="Respuesta nueva"
                  />
                )}
              </button>
            ))}
          </div>

          <div
            role="tabpanel"
            id="panel-retro"
            aria-labelledby="pestana-retro"
            hidden={pestana !== "retro"}
            className="flex min-h-0 flex-1 flex-col"
          >
            {retro(
              <button
                type="button"
                onClick={() => setPestana("coach")}
                className="absolute bottom-4 right-4 flex items-center gap-2 rounded-full bg-[var(--primary)] px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition hover:brightness-110"
              >
                <MessageCircle size={16} />
                Preguntar al coach
              </button>,
              retros.length > 1 && <div className="mb-4 flex justify-end">{selector}</div>,
            )}
          </div>
          {/* El chat sigue montado al cambiar de pestaña para no perder la conversación en curso. */}
          <div
            role="tabpanel"
            id="panel-coach"
            aria-labelledby="pestana-coach"
            hidden={pestana !== "coach"}
            className="flex min-h-0 flex-1 flex-col"
          >
            <CoachPanel>{chat}</CoachPanel>
          </div>

          {!(pestana === "coach" && composerEnfocado) && (
            <div className="flex flex-shrink-0 gap-2 border-t border-[var(--border)] bg-[var(--card-background)] px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <DescargarRetroMenu
                idActividadAprendiz={actividadAprendiz?.id}
                actividadNombre={actividad.nombre}
                disabled={isStreaming}
                onDescargarReto={onDescargarReto}
                className="flex-1"
              />
              {volverAIntentar("flex-1")}
            </div>
          )}
        </>
      )}
    </div>
  );
}

